import React from 'react';
import { Alert, Box, Button, Paper, Stack, Typography } from '@mui/material';
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EmailIcon from '@mui/icons-material/Email';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

/**
 * Spike 1 §1.5 — "Best on desktop or tablet" overlay.
 *
 * Mounts over a heavy-creation operation panel when the user is on a
 * mobile-shell viewport. v3.0 ships read-only canvas preview + an override
 * affordance for power users + an email-me-a-link low-friction handoff to
 * desktop. The deep-link generation and email send are stubs for v3.0
 * (`onEmailLink` is wired but the BE endpoint lands in a follow-on slice).
 *
 * Heavy ops in scope (per spike §1.2):
 *   Konva mask brush, Inpaint, Outpaint, multi-layer edits, sketch input,
 *   precise color picking, TryOn upload-and-pose flows.
 */
interface Props {
  /** The operation the user landed on — used in the overlay copy. */
  operationLabel: string;
  /**
   * The full URL to deep-link back to this exact view on desktop. Default is
   * the current location.href; pass an explicit value when the parent has a
   * canonical share URL it'd rather emit.
   */
  desktopLink?: string;
  /** Called when the user accepts the deferral and asks for the read-only path. */
  onViewReadOnly?: () => void;
  /**
   * Called when the user explicitly overrides the deferral ("Continue anyway").
   * The parent typically toggles a state flag so the operation panel renders
   * its full (but probably awkward on mobile) UI.
   */
  onContinueAnyway?: () => void;
  /**
   * Called when the user picks "Email me a link". v3.0 hands the URL off to
   * the user's default mail client via `mailto:`; v3.1 will use a BE send-
   * email endpoint so the message doesn't depend on the client setup.
   */
  onEmailLink?: (link: string) => void;
}

export const HeavyCreationOverlay: React.FC<Props> = ({
  operationLabel,
  desktopLink,
  onViewReadOnly,
  onContinueAnyway,
  onEmailLink,
}) => {
  const link = desktopLink ?? (typeof window !== 'undefined' ? window.location.href : '');

  const handleEmailLink = () => {
    if (onEmailLink) {
      onEmailLink(link);
      return;
    }
    // Default fallback — open the user's mail client with a pre-filled body.
    if (typeof window !== 'undefined') {
      const subject = encodeURIComponent(`Coral Creator — open ${operationLabel} on desktop`);
      const body = encodeURIComponent(
        `Open this on desktop or tablet to use ${operationLabel}:\n\n${link}\n`,
      );
      window.location.href = `mailto:?subject=${subject}&body=${body}`;
    }
  };

  return (
    <Box
      role="dialog"
      aria-modal="false"
      aria-label={`${operationLabel} requires a larger device`}
      sx={{
        position: 'absolute',
        inset: 0,
        zIndex: theme => theme.zIndex.modal,
        // Slight scrim so the underlying canvas peeks through as a static
        // preview. Full-bleed background would feel like a hard wall.
        bgcolor: 'rgba(0,0,0,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
        // The overlay is mountable on any panel — positioned relative to its
        // parent (the parent declares `position: relative`).
      }}
    >
      <Paper
        elevation={6}
        sx={{
          maxWidth: 380,
          width: '100%',
          p: 3,
          borderRadius: 2,
        }}
      >
        <Stack spacing={2} alignItems="flex-start">
          <Stack direction="row" spacing={1} alignItems="center">
            <DesktopWindowsIcon color="primary" />
            <Typography variant="h6">Best on desktop or tablet</Typography>
          </Stack>

          <Typography variant="body2" color="text.secondary">
            Mask brushing, multi-layer edits, and precise sketch input use a level
            of pointer precision that's hard to deliver on phones. Open Coral
            Creator on a larger device to use {operationLabel}.
          </Typography>

          <Alert severity="info" variant="outlined" sx={{ width: '100%', py: 0.5 }}>
            Your work is saved — picking it up on desktop continues right where
            you left off.
          </Alert>

          <Stack direction="column" spacing={1} sx={{ width: '100%' }}>
            <Button
              variant="contained"
              startIcon={<VisibilityIcon />}
              onClick={onViewReadOnly}
              fullWidth
            >
              View read-only
            </Button>
            <Button
              variant="outlined"
              startIcon={<EmailIcon />}
              onClick={handleEmailLink}
              fullWidth
            >
              Email me a link
            </Button>
            <Button
              variant="text"
              size="small"
              startIcon={<OpenInNewIcon fontSize="small" />}
              onClick={onContinueAnyway}
              fullWidth
              sx={{ color: 'text.secondary' }}
            >
              Continue anyway (best on a larger device)
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
};
